// fetches pre-joined schedule rows from dedicated endpoint
// replaces useReceiverScheduleData + useApprovedRequestsInfo + buildApprovedRows

import { useCallback, useEffect, useRef, useState } from "react";
import { apiGetJson } from "../api/client";
import { useDataCache, CACHE_KEYS } from "../contexts/DataCacheContext";

const CACHE_KEY = CACHE_KEYS.RECEIVER_SCHEDULE;

// "HH:MM:SS" pair → "9:00 AM – 5:00 PM"
function fmtTime(raw) {
  if (!raw) return null;
  const [h, m] = String(raw).split(":");
  const hr = Number(h);
  if (Number.isNaN(hr)) return String(raw);
  const suffix = hr >= 12 ? "PM" : "AM";
  return `${hr % 12 || 12}:${m} ${suffix}`;
}

function buildTimeWindow(start, end) {
  const s = fmtTime(start);
  const e = fmtTime(end);
  if (s && e) return `${s} – ${e}`;
  return s || e || "—";
}

function mapRows(raw) {
  const today = new Date().toISOString().slice(0, 10);

  return (raw || []).map((r) => {
    const confirmed = Number(r.pickup_confirmed ?? 0);
    const delivered = Boolean(r.date_delivered);

    // derive status
    let status;
    if (delivered) {
      status = "delivered";
    } else if (confirmed === 1) {
      const sched = r.scheduled_date ? String(r.scheduled_date).slice(0, 10) : null;
      status = sched && sched < today ? "late" : "scheduled";
    } else {
      status = "pending";
    }

    // scheduled date: use request's date when confirmed, else donor's latest_pickup
    const scheduledDate = confirmed === 1 ? (r.scheduled_date || null) : (r.latest_pickup || null);

    // time window: prefer request's time when confirmed, else listing's pickup times
    const listingTime = buildTimeWindow(r.pickup_time_start, r.pickup_time_end);
    const timeWindow = confirmed === 1
      ? (r.time && r.time !== "—" ? r.time : listingTime)
      : listingTime;

    return {
      id: r.listing_id,
      requestId: r.request_id || null,
      material: r.material || "—",
      status,
      donor: r.donor || "—",
      scheduledDate,
      timeWindow,
      timeStart: r.pickup_time_start || null,
      timeEnd: r.pickup_time_end || null,
      requestedQty: r.max_qty || 0,
      allocatedQty: r.quantity || 0,
      dateReceived: r.date_delivered || null,
    };
  });
}

export function useScheduleData() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const { getCachedData, setCachedData } = useDataCache();
  const getCacheRef = useRef(getCachedData);
  const setCacheRef = useRef(setCachedData);
  useEffect(() => { getCacheRef.current = getCachedData; }, [getCachedData]);
  useEffect(() => { setCacheRef.current = setCachedData; }, [setCachedData]);

  const performRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
    return Promise.resolve();
  }, []);

  useEffect(() => {
    let cancelled = false;

    // on initial mount, serve cache and stop — no background fetch
    if (refreshKey === 0) {
      const cached = getCacheRef.current?.(CACHE_KEY);
      if (cached?.data && Array.isArray(cached.data) && cached.data.length > 0) {
        setRows(mapRows(cached.data));
        setLastUpdated(cached.timestamp || null);
        setLoading(false);
        return;
      }
    }

    async function load() {
      if (!cancelled) {
        setError("");
        setLoading(true);
      }

      try {
        const res = await apiGetJson("/material_management/schedule/rows");
        if (cancelled) return;

        const raw = res?.rows || [];
        const ts = Date.now();

        setRows(mapRows(raw));
        setLastUpdated(ts);

        try { setCacheRef.current?.(CACHE_KEY, raw, ts); } catch { /* ignore */ }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load schedule data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { rows, loading, error, lastUpdated, performRefresh, setRows };
}
